<template>
    <label
        class="ds-checkbox"
        :class="[wrapperClass, rootClasses]"
        :data-test="dataTestLabel"
        :data-testid="dataTestid"
    >
        <input
            type="checkbox"
            class="ds-checkbox__input"
            :id
            :name="id"
            :disabled
            :required
            v-model="modelValue"
            v-bind="attrs"
        />
        <div
            class="ds-checkbox__checkbox"
            tabindex="0"
            @keydown="checkboxKeydownEventHandler"
            role="checkbox"
            :aria-checked="modelValue"
            :aria-required="required"
            :aria-disabled="disabled"
            :aria-invalid="!isValid"
            :aria-labelledby="ariaLabelId"
        >
            <DsIcon
                :width="16"
                :height="16"
                name="check"
                class="ds-checkbox__checkmark"
                :is-in-storybook="isInStorybook"
            />
        </div>
        <div class="ds-checkbox__texts" :data-test="dataTestTexts">
            <span class="ds-checkbox__label" :id="ariaLabelId" v-html="label"></span>
            <span
                v-if="errorMessage || supportingText"
                class="ds-checkbox__message"
                :class="{ 'ds-checkbox__message--floating': !supportingText }"
                v-html="errorMessage || supportingText"
                role="alert"
                aria-live="polite"
                aria-atomic="true"
            ></span>
        </div>
        <slot></slot>
    </label>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from "vue";
import DsIcon from "../../icon/ds-icon.vue";
import { Theme } from "../../types/theme";

const props = defineProps<{
    /**
     * Unique component identifier. This identifier is nessary for accessibility.
     */
    id: string;

    /*
     * Label text.
     */
    label: string;

    /*
     * Supporting text below the label.
     */
    supportingText?: string;

    /**
     * The error message for the checkbox. If provided, the checkbox is invalid and it will display the error message instead of the supporting text.
     */
    errorMessage?: string;

    /**
     * Disable the checkbox. Default is false.
     */
    disabled?: boolean;

    /**
     * The checkbox must be checked. Default is false.
     */
    required?: boolean;

    /**
     * Theme for the component. Defaults to "light" theme.
     */
    theme?: Theme;

    /**
     * Additional data-test attribute on lebel element for testing purposes.
     */
    dataTestLabel?: string;

    /**
     * Additional data-test attribute on texts wrapper element for testing purposes.
     */
    dataTestTexts?: string;

    /**
     * Indicates whether the component is rendered within Storybook. This prop is needed to DsIcon component for correct rendering.
     */
    isInStorybook?: boolean;

    dataTestid?: string;
}>();

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

/**
 * The v-model for the checkbox.
 */
const modelValue = defineModel<boolean>();

/**
 * The error messages for the checkbox. If array isn't empty, the checkbox is invalid and it will display the error message instead of the supporting text.
 */

const isValid = computed(() => !props.errorMessage);

const checkboxKeydownEventHandler = (event: KeyboardEvent) => {
    if (!props.disabled && (event.key === "Enter" || event.key === "Space")) {
        modelValue.value = !modelValue.value;
    }
};

const ariaLabelId = computed(() => `label-${props.id.replace(/\s+/g, "-").toLowerCase()}`);

const rootClasses = computed(() => {
    return [
        props.theme === "dark" ? "theme--dark" : "",
        props.disabled ? "disabled" : "",
        !isValid.value ? "has-error" : "",
        modelValue.value ? "checked" : "",
    ];
});
</script>
